import { ref, computed } from 'vue'
import { defineStore } from 'pinia'
import {
  PublicClientApplication,
  type AccountInfo,
  type AuthenticationResult,
  AuthError,
  BrowserAuthError,
  InteractionRequiredAuthError,
  ServerError,
} from '@azure/msal-browser'
import { msalConfig, loginRequest, tokenRequest, iframeRedirectUri } from '@/auth/msalConfig'
import { logAuthEvent } from '@/api/telemetry'

export const useAuthStore = defineStore('auth', () => {
  const msalInstance = new PublicClientApplication(msalConfig)
  const account = ref<AccountInfo | null>(null)
  const initialized = ref(false)

  const isAuthenticated = computed(() => account.value !== null)

  async function initialize() {
    await msalInstance.initialize()
    try {
      const response = await msalInstance.handleRedirectPromise({ navigateToLoginRequestUrl: false })
      if (response?.account) {
        msalInstance.setActiveAccount(response.account)
        account.value = response.account
        logAuthEvent({ event: 'msal_initialize_redirect_handled', hasAccount: true })
      } else {
        const accounts = msalInstance.getAllAccounts()
        if (accounts.length > 0) {
          msalInstance.setActiveAccount(accounts[0] ?? null)
          account.value = accounts[0] ?? null
          logAuthEvent({ event: 'msal_initialize_cached_account', hasAccount: true })
        } else {
          logAuthEvent({ event: 'msal_initialize_no_account', hasAccount: false })
        }
      }
    } catch (error) {
      logAuthEvent({
        event: 'msal_initialize_failed',
        ...describeError(error),
      })
    }
    initialized.value = true
  }

  async function login() {
    await msalInstance.loginRedirect(loginRequest)
  }

  async function logout() {
    await msalInstance.logoutRedirect({
      account: account.value ?? undefined,
    })
    account.value = null
  }

  const interactionRequiredCodes = /invalid_grant|login_required|interaction_required|consent_required|AADSTS50058|AADSTS50076|AADSTS50079|AADSTS65001|AADSTS700082/
  // BrowserAuthErrors that are transient / not auth-related — do NOT redirect on these.
  const benignBrowserAuthCodes = new Set([
    'no_network_connectivity',
    'network_error',
    'post_request_failed',
    'endpoints_resolution_error',
  ])
  let redirectInFlight = false

  async function getAccessToken(forceRefresh = false): Promise<string | null> {
    if (!account.value) {
      logAuthEvent({ event: 'getToken_no_account', forceRefresh, hasAccount: false })
      return null
    }
    if (redirectInFlight) {
      logAuthEvent({ event: 'getToken_redirect_in_flight', forceRefresh, hasAccount: true })
      return null
    }
    try {
      const response: AuthenticationResult = await msalInstance.acquireTokenSilent({
        ...tokenRequest,
        account: account.value,
        forceRefresh,
        redirectUri: iframeRedirectUri,
      })
      return response.accessToken
    } catch (error) {
      const details = describeError(error)
      logAuthEvent({
        event: 'acquireTokenSilent_failed',
        forceRefresh,
        hasAccount: true,
        accountHomeId: account.value?.homeAccountId,
        ...details,
      })

      // Redirect in any interaction-required / auth-level failure so we don't silently
      // fire API calls without a bearer token. Skip only clearly-transient network errors.
      const shouldRedirect =
        error instanceof InteractionRequiredAuthError ||
        (error instanceof ServerError &&
          interactionRequiredCodes.test(`${error.errorCode} ${error.errorMessage}`)) ||
        (error instanceof BrowserAuthError && error.errorCode === 'monitor_window_timeout') ||
        // Catch-all for any other AuthError that isn't a known transient network issue.
        (error instanceof AuthError &&
          !(error instanceof BrowserAuthError && benignBrowserAuthCodes.has(error.errorCode)))

      if (shouldRedirect) {
        logAuthEvent({
          event: 'acquireTokenRedirect_triggered',
          forceRefresh,
          hasAccount: true,
          accountHomeId: account.value?.homeAccountId,
          ...details,
        })
        redirectInFlight = true
        await msalInstance.acquireTokenRedirect({ ...tokenRequest, account: account.value })
      } else {
        console.error('acquireTokenSilent failed (transient, not redirecting)', error)
      }
      return null
    }
  }

  return {
    account,
    initialized,
    isAuthenticated,
    initialize,
    login,
    logout,
    getAccessToken,
  }
})

function describeError(error: unknown): { errorType: string; errorCode: string; errorMessage: string } {
  if (error instanceof AuthError) {
    return {
      errorType: error.constructor.name,
      errorCode: error.errorCode ?? '',
      errorMessage: error.errorMessage ?? error.message ?? '',
    }
  }
  if (error instanceof Error) {
    return {
      errorType: error.constructor.name,
      errorCode: '',
      errorMessage: error.message,
    }
  }
  return { errorType: typeof error, errorCode: '', errorMessage: String(error) }
}
